import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Phone, Users, CreditCard } from "lucide-react";

const CalendarPage = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState("month"); // month, week
  const [filter, setFilter] = useState("all"); // all, meetings, calls, renewals
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("token");

  const fetchCalendarData = useCallback(async () => {
    setLoading(true);
    try {
      const [leadsRes, subscriptionsRes] = await Promise.all([
        axios.get(`${API}/leads`, { headers: { Authorization: `Bearer ${token}` } }),
        user?.role === "manager" 
          ? axios.get(`${API}/subscriptions`, { headers: { Authorization: `Bearer ${token}` } })
          : Promise.resolve({ data: [] })
      ]);

      const calendarEvents = [];

      // Extract meetings from leads
      leadsRes.data.forEach(lead => {
        if (lead.meetings) {
          lead.meetings.forEach((meeting, idx) => {
            if (meeting.date) {
              calendarEvents.push({
                id: `meeting-${lead.lead_id}-${idx}`,
                type: "meeting",
                title: `Meeting: ${lead.company_name}`,
                subtitle: `${lead.first_name} ${lead.last_name}`,
                date: new Date(meeting.date),
                notes: meeting.notes,
                color: "bg-purple-500"
              });
            }
          });
        }

        // Extract scheduled calls from leads
        if (lead.scheduled_calls) {
          lead.scheduled_calls.forEach((call, idx) => {
            if (call.date) {
              calendarEvents.push({
                id: `call-${lead.lead_id}-${idx}`,
                type: "call",
                title: `Call: ${lead.company_name}`,
                subtitle: `${lead.first_name} ${lead.last_name}`,
                date: new Date(call.date),
                notes: call.notes,
                color: "bg-blue-500"
              });
            }
          });
        }
      });

      // Extract renewal dates from subscriptions (manager only)
      if (subscriptionsRes.data) {
        subscriptionsRes.data.forEach(sub => {
          if (sub.renewals) {
            sub.renewals.forEach((renewal, idx) => {
              if (renewal.renewal_date && !renewal.paid) {
                calendarEvents.push({
                  id: `renewal-${sub.subscription_id}-${idx}`,
                  type: "renewal",
                  title: `Renewal: ${sub.business_name}`,
                  subtitle: `$${parseFloat(renewal.renewal_price || 0).toLocaleString()}`,
                  date: new Date(renewal.renewal_date),
                  notes: renewal.notes,
                  color: "bg-emerald-500",
                  likeliness: renewal.likeliness
                });
              }
            });
          }
        });
      }

      setEvents(calendarEvents);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("token");
        setUser(null);
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  }, [token, user, setUser, navigate]);

  useEffect(() => {
    fetchCalendarData();
  }, [fetchCalendarData]);

  const handleLogout = async () => {
    localStorage.removeItem("token");
    setUser(null);
    navigate("/login");
  };

  // Navigation functions
  const goToToday = () => setCurrentDate(new Date());
  
  const goToPrevious = () => {
    const newDate = new Date(currentDate);
    if (view === "month") {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setDate(newDate.getDate() - 7);
    }
    setCurrentDate(newDate);
  };

  const goToNext = () => {
    const newDate = new Date(currentDate);
    if (view === "month") {
      newDate.setMonth(newDate.getMonth() + 1);
    } else {
      newDate.setDate(newDate.getDate() + 7);
    }
    setCurrentDate(newDate);
  };

  // Filter events
  const filteredEvents = events.filter(event => {
    if (filter === "all") return true;
    if (filter === "meetings") return event.type === "meeting";
    if (filter === "calls") return event.type === "call";
    if (filter === "renewals") return event.type === "renewal";
    return true;
  });

  // Get days for current view
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPadding = firstDay.getDay();
    const days = [];

    // Add padding for previous month
    for (let i = startPadding - 1; i >= 0; i--) {
      const d = new Date(year, month, -i);
      days.push({ date: d, isCurrentMonth: false });
    }

    // Add days of current month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }

    // Add padding for next month
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }

    return days;
  };

  const getWeekDays = (date) => {
    const days = [];
    const startOfWeek = new Date(date);
    startOfWeek.setDate(date.getDate() - date.getDay());

    for (let i = 0; i < 7; i++) {
      const d = new Date(startOfWeek);
      d.setDate(startOfWeek.getDate() + i);
      days.push({ date: d, isCurrentMonth: true });
    }
    return days;
  };

  const getEventsForDay = (date) => {
    return filteredEvents.filter(event => {
      const eventDate = new Date(event.date);
      return eventDate.getFullYear() === date.getFullYear() &&
             eventDate.getMonth() === date.getMonth() &&
             eventDate.getDate() === date.getDate();
    });
  };

  const isToday = (date) => {
    const today = new Date();
    return date.getFullYear() === today.getFullYear() &&
           date.getMonth() === today.getMonth() &&
           date.getDate() === today.getDate();
  };

  const formatMonthYear = (date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const formatWeekRange = (date) => {
    const startOfWeek = new Date(date);
    startOfWeek.setDate(date.getDate() - date.getDay());
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    return `${startOfWeek.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endOfWeek.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  const days = view === "month" ? getDaysInMonth(currentDate) : getWeekDays(currentDate);
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Count events by type
  const meetingCount = events.filter(e => e.type === "meeting").length;
  const callCount = events.filter(e => e.type === "call").length;
  const renewalCount = events.filter(e => e.type === "renewal").length;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex">
        <Sidebar user={user} setUser={setUser} onLogout={handleLogout} />
        <div className="flex-1 flex items-center justify-center ml-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="calendar-page">
      <Sidebar user={user} setUser={setUser} onLogout={handleLogout} />
      <div className="flex-1 p-8 ml-64">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Calendar</h1>
            <p className="text-slate-500 mt-1">View meetings, calls, and renewals</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 border border-slate-200 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
              <Users className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{meetingCount}</p>
              <p className="text-sm text-slate-500">Meetings</p>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-200 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
              <Phone className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{callCount}</p>
              <p className="text-sm text-slate-500">Scheduled Calls</p>
            </div>
          </div>
          {user?.role === "manager" && (
            <div className="bg-white rounded-xl p-4 border border-slate-200 flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{renewalCount}</p>
                <p className="text-sm text-slate-500">Upcoming Renewals</p>
              </div>
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="bg-white rounded-xl border border-slate-200 mb-6">
          <div className="flex items-center justify-between p-4 border-b border-slate-200">
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={goToToday} data-testid="today-btn">
                Today
              </Button>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" onClick={goToPrevious} data-testid="prev-btn">
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <Button variant="ghost" size="icon" onClick={goToNext} data-testid="next-btn">
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>
              <h2 className="text-xl font-semibold text-slate-900">
                {view === "month" ? formatMonthYear(currentDate) : formatWeekRange(currentDate)}
              </h2>
            </div>
            <div className="flex items-center gap-3">
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-44" data-testid="filter-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Events</SelectItem>
                  <SelectItem value="meetings">Meetings Only</SelectItem>
                  <SelectItem value="calls">Scheduled Calls Only</SelectItem>
                  {user?.role === "manager" && (
                    <SelectItem value="renewals">Renewals Only</SelectItem>
                  )}
                </SelectContent>
              </Select>
              <Select value={view} onValueChange={setView}>
                <SelectTrigger className="w-36" data-testid="view-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="month">Monthly View</SelectItem>
                  <SelectItem value="week">Weekly View</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="p-4">
            {/* Week day headers */}
            <div className="grid grid-cols-7 mb-2">
              {weekDays.map(day => (
                <div key={day} className="text-center text-sm font-medium text-slate-500 py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar days */}
            <div className={`grid grid-cols-7 gap-1 ${view === "week" ? "min-h-[500px]" : ""}`}>
              {days.map((day, idx) => {
                const dayEvents = getEventsForDay(day.date);
                const showMore = dayEvents.length > 3;
                const displayEvents = view === "week" ? dayEvents : dayEvents.slice(0, 3);

                return (
                  <div
                    key={idx}
                    className={`
                      ${view === "month" ? "min-h-[120px]" : "min-h-[500px]"}
                      p-2 border border-slate-100 rounded-lg
                      ${day.isCurrentMonth ? "bg-white" : "bg-slate-50"}
                      ${isToday(day.date) ? "ring-2 ring-indigo-500" : ""}
                    `}
                  >
                    <div className={`text-sm font-medium mb-1 ${
                      isToday(day.date) 
                        ? "text-indigo-600" 
                        : day.isCurrentMonth 
                          ? "text-slate-900" 
                          : "text-slate-400"
                    }`}>
                      {day.date.getDate()}
                    </div>
                    <div className="space-y-1">
                      {displayEvents.map(event => (
                        <div
                          key={event.id}
                          className={`${event.color} text-white text-xs p-1.5 rounded truncate cursor-pointer hover:opacity-90`}
                          title={`${event.title}\n${event.subtitle}\n${event.notes || ''}`}
                        >
                          <div className="font-medium truncate">{event.title}</div>
                          {view === "week" && (
                            <div className="text-white/80 truncate">{event.subtitle}</div>
                          )}
                        </div>
                      ))}
                      {showMore && view === "month" && (
                        <div className="text-xs text-slate-500 font-medium">
                          +{dayEvents.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-purple-500"></div>
            <span className="text-sm text-slate-600">Meetings</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-blue-500"></div>
            <span className="text-sm text-slate-600">Scheduled Calls</span>
          </div>
          {user?.role === "manager" && (
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-emerald-500"></div>
              <span className="text-sm text-slate-600">Subscription Renewals</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CalendarPage;
